import { Injectable } from '@angular/core';
import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { environment } from 'environment/environment';

@Injectable({
  providedIn: 'root',
})
/**
 * Service for monitoring and logging application telemetry data using Azure Application Insights.
 * This service supports tracking page views, custom events, metrics, exceptions, and traces.
 */
export class MonitoringService {
  private appInsights!: ApplicationInsights;

  /**
   * Initializes the Application Insights SDK with configuration settings.
   * Auto-tracks route changes, HTTP requests, and custom telemetry events.
   */
  constructor() {
    if (environment.enableMonitoring) {
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString: environment.appInsights.connectionString,
          enableAutoRouteTracking: true, // Automatically tracks Angular route changes
          disableFetchTracking: false, // Tracks HTTP fetch requests automatically
          enableCorsCorrelation: true, // Enables cross-domain tracing
          enableRequestHeaderTracking: true, // Logs HTTP request headers
          enableResponseHeaderTracking: true, // Logs HTTP response headers
          distributedTracingMode: 2, // Uses W3C Trace Context for distributed tracing
          autoTrackPageVisitTime: true, // Tracks page visit duration automatically
          loggingLevelConsole: 2, // Logs warnings and errors in the console
          maxBatchInterval: 15000, // Sends telemetry every 15 seconds
          samplingPercentage: 100,
          excludeRequestFromAutoTrackingPatterns: [/launchdarkly/, /auth\.nexusyms/],
          correlationHeaderExcludePatterns: [/launchdarkly/, /auth\.nexusyms/],
        },
      });

      this.appInsights.loadAppInsights();
      this.addCustomTelemetryInitializer();
    }
  }

  /**
   * Logs a page view event with optional custom properties.
   * Use this to manually track page views when automatic tracking is insufficient.
   *
   * @param name - Name of the page being viewed.
   * @param url - URL of the page being viewed.
   * @param customProps - Additional custom properties to attach.
   */
  public logPageView(name?: string, url?: string, customProps?: Record<string, unknown>): void {
    if (!environment.enableMonitoring) return;
    this.appInsights.trackPageView({
      name,
      uri: url,
      properties: customProps,
    });
  }

  /**
   * Logs a custom event with associated properties.
   * Use this to log business-specific events such as user interactions.
   *
   * @param name - Name of the event.
   * @param properties - Custom properties to provide additional event context.
   */
  public logEvent(name: string, properties?: Record<string, unknown>): void {
    if (!environment.enableMonitoring) return;
    this.appInsights.trackEvent({ name }, properties);
  }

  /**
   * Logs a custom metric for performance or monitoring purposes.
   * Useful for tracking numeric values like load times or counts.
   *
   * @param name - Name of the metric.
   * @param average - The average value of the metric.
   * @param properties - Additional properties providing context for the metric.
   */
  public logMetric(name: string, average: number, properties?: Record<string, unknown>): void {
    if (!environment.enableMonitoring) return;
    this.appInsights.trackMetric({ name, average }, properties);
  }

  /**
   * Logs an exception with a severity level and additional context.
   * This helps monitor unexpected failures or errors in the application.
   *
   * @param exception - The exception object to log.
   * @param severityLevel - Severity level from 0 (Verbose) to 4 (Critical).
   * @param properties - Additional custom properties related to the exception.
   */
  public logException(exception: Error, severityLevel = 3, properties?: Record<string, unknown>): void {
    if (!environment.enableMonitoring) return;
    this.appInsights.trackException({
      exception,
      severityLevel,
      properties,
    });
  }

  /**
   * Logs custom traces or diagnostic messages.
   * Useful for tracking application flow or debugging issues.
   *
   * @param message - Message describing the trace.
   * @param properties - Additional context properties.
   */
  public logTrace(message: string, properties?: Record<string, unknown>): void {
    if (!environment.enableMonitoring) return;
    this.appInsights.trackTrace({ message }, properties);
  }

  /**
   * Sets the authenticated user context, associating telemetry data with a specific user.
   * This can help track user-specific behaviors or issues.
   *
   * @param userId - Unique identifier of the user.
   * @param accountId - Optional account ID related to the user.
   */
  public setUserContext(userId: string, accountId?: string): void {
    if (!environment.enableMonitoring) return;
    this.appInsights.setAuthenticatedUserContext(userId, accountId);
  }

  /**
   * Clears the current authenticated user context.
   * Use this when the user logs out or the session ends.
   */
  public clearUserContext(): void {
    if (!environment.enableMonitoring) return;
    this.appInsights.clearAuthenticatedUserContext();
  }

  /**
   * Adds a custom telemetry initializer to enrich telemetry data with common tags.
   * This method injects metadata such as cloud role, role instance, and filters unwanted events.
   */
  private addCustomTelemetryInitializer(): void {
    this.appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
      envelope.tags = {
        ...envelope.tags,
        'ai.cloud.role': 'SportlightUI',
      };

      return true;
    });
  }
}
